import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import * as Enums from "@/utils/enums";
import { useForm } from "@mantine/form";
import { Box, Checkbox, Flex, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import Fetch from "@/utils/Fetch";
import { getDefaultSectionObject } from "@/PageComponents/SectionTable/SectionTable";
import BundleSelector from "@/PageComponents/Bundle/BundleSelector";
import ScTextControl from "@/components/sc-controls/form-controls/v2/sc-text-control";
import WarehouseSelector from "@/components/selectors/warehouse/warehouse-selector";
import featureService from "@/services/feature/feature-service";
import constants from "@/utils/constants";
import helper from "@/utils/helper";
import { calculateLineTotal, roundPrice } from '@/utils/line-totals';
import { Warehouse } from "@/interfaces/api/models";
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import stockService from "@/services/stock/stock-service";

const AddBundleToSectionListForm: FC<{
    isNew: boolean
    itemID: string
    module: number
    accessStatus: number
    items: any[]
    // increment start at 0, if this changes, validation and onSave event will be triggered
    triggerSave: number
    onSaveItems: (newItems: any[]) => void
    companyTaxPercentage: number
    defaultSectionPdfSettings?: { HideLineItems: boolean; DisplaySubtotal: boolean; }
    // isNew: boolean
    // jobInventoryItem: any
    // updateJobInventoryExternally: (itemToSave: any) => void
    // onJobInventoryItemsSaved: (newJobInventories: any[], newJobRowVersion: any) => void
    // jobQueryData: any
    // accessStatus: number
    // jobSingleItem: any
    // linkedProductIDs: any
    // type: 'Job' | 'Recurring'
    // jobItemSelection: any
    // jobItemOrder: any
    // filteredStockItemStatus: any
    // jobInventoryList: any[]
    // onClose: () => void
    // fromCreateJob: boolean
    // fromStatusChange: boolean
    // fromSchedule?: boolean
    // tableSectionItem?: any
    storeID: string | undefined
    onSuppressSave: (suppressSave: boolean) => void
    warehouse?: Warehouse
}> = (props: any, context: any) => {

    const { data: stockControlFeatureData } = useQuery(['hasStockControl'], () => featureService.getFeature(constants.features.STOCK_CONTROL));
    const hasStockControl = !!stockControlFeatureData && [Enums.Module.JobCard, Enums.Module.Invoice].includes(props.module);


    /*const saveInventoryUsedMutation = useMutation(['inventoryItemsUsed'], ({jobInventory, sections, jobId}: any) => postItemsUsed(jobInventory, sections, jobId), {
        onSuccess: data => {
            props.onJobInventoryItemsSaved(data.sortedResults, data.rowVersion)
            showNotification({
                id: 'AddInventoryItemWorkedOn',
                message: `Inventory materials updated `,
                color: 'scBlue',
            });
            props.onClose()
        },
        onError: (error: any) => {
            showNotification({
                id: 'AddInventoryItemNotification',
                message: error?.message || `Inventory items could not be added `,
                color: 'red',
            });
        }
    });*/

    const getSelectedWarehouse = () => {
        if (props.module === Enums.Module.JobCard) {
            return props.warehouse;
        }
        return form.values.warehouse;
    };

    const form = useForm({
        initialValues: {
            bundle: null,
            section: props.module === Enums.Module.Quote || props.module === Enums.Module.Invoice,
            sectionName: '',
            warehouse: null as any
        },
        validate: {
            bundle: (x: any) => { return !!x ? null : 'Please select a bundle to add'; },
            warehouse: (x: any) => { return !hasStockControl || !!getSelectedWarehouse() ? null : 'Warehouse missing'; },
        }
    })

    const [inventoryItems, setInventoryItems] = useState<any[]>([])

    const [stockQtyMap, setStockQtyMap] = useState<Record<string, number | null>>({})
    const fetchedStockIDsRef = useRef<Set<string>>(new Set())

    useEffect(() => {
        const trackedIDs = [...new Set(
            inventoryItems
                .filter((x: any) => x.Inventory?.IsQuantityTracked && x.Inventory?.ID)
                .map((x: any) => x.Inventory.ID as string)
        )].filter((id: string) => !fetchedStockIDsRef.current.has(id))

        if (trackedIDs.length === 0) return

        trackedIDs.forEach((id: string) => {
            fetchedStockIDsRef.current.add(id)
            stockService.getWarehouseStockForInventory(id)
                .then((stocks: any[]) => {
                    const total = (stocks ?? []).reduce((sum: number, s: any) => sum + (s.QuantityOnHand ?? 0), 0)
                    setStockQtyMap(prev => ({ ...prev, [id]: total }))
                })
                .catch(() => {
                    setStockQtyMap(prev => ({ ...prev, [id]: null }))
                })
        })
    }, [inventoryItems])

    const trackedItems = useMemo(() =>
        inventoryItems.filter((x: any) => x.Inventory?.IsQuantityTracked),
        [inventoryItems]
    )

    const hasTrackedItems = trackedItems.length > 0

    const stockDataReady = useMemo(() =>
        trackedItems.every((x: any) => x.Inventory?.ID in stockQtyMap),
        [trackedItems, stockQtyMap]
    )

    const hasExceededStock = useMemo(() =>
        trackedItems.some((x: any) => {
            const qty = stockQtyMap[x.Inventory?.ID]
            return qty !== null && x.Quantity > (qty ?? 0)
        }),
        [trackedItems, stockQtyMap]
    )

    const inventoryItemsQuery = useQuery(['bundleInventoryItems', form.values.bundle],
        () => Fetch.get({
            url: '/Bundle',
            params: {
                id: form.values.bundle
            }
        } as any),
        {
            enabled: !!form.values.bundle
        })

    useEffect(() => {
        if (inventoryItemsQuery.data) {
            const data = inventoryItemsQuery.data
            if (data?.Name) {
                form.setFieldValue('sectionName', data.Name)
                setInventoryItems(data.BundleInventory.sort((a: any, b: any) => a.LineNumber - b.LineNumber).map((x: any) => x))
            }
        }
    }, [inventoryItemsQuery.data])

    useEffect(() => {
        if (!form.values.bundle) {
            setInventoryItems([])
        }
    }, [form.values.bundle])

    const handleSubmit = async (values: any) => {
        let validateResult = form.validate();
        if (!validateResult.hasErrors) {

            /*const bestPredictedDefaultPdfSettingValues = {
                HideLineItems:
                    globalHideLineItemsIndeterminate ? sectionData.filter(x => x.HideLineItems) < sectionData.filter(x => !x.HideLineItems) :
                        globalHideLineItemsChecked,
                DisplaySubtotal:
                    globalDisplaySubtotalIndeterminate ? sectionData.filter(x => x.DisplaySubtotal) < sectionData.filter(x => !x.DisplaySubtotal) :
                        globalDisplaySubtotalChecked,
            }*/

            const section =
                values.section ?
                    {
                        ...getDefaultSectionObject(crypto.randomUUID(), props.module, props.itemID, values.sectionName || inventoryItemsQuery.data?.Name, props.defaultSectionPdfSettings)
                    } : null

            const newItemsList = [
                ...props.items,
                ...inventoryItems.sort((a: any, b: any) => a.LineNumber - b.LineNumber).map((x: any, i: any) => {
                    const inventory = x.Inventory
                    let lineTotalExclusive = 0;

                    let priceExclusive = !x.UsePriceList ? roundPrice(x.CustomPrice) : roundPrice(x.Inventory.ListPrice - (x.Inventory.ListPrice * (x.PriceListDiscount / 100)));

                    if (props.module === Enums.Module.PurchaseOrder) {
                        priceExclusive = roundPrice(inventory.CostPrice);
                    }

                    lineTotalExclusive = calculateLineTotal({ quantity: x.Quantity, unitPrice: priceExclusive });
                    // STOCK CONTROL ISQUANTITYTRACKED CHANGE
                    return (
                        {
                            // ...x,
                            Description: /*x.ProductID ? x.Inventory.Description + ' - ' + x.ProductNumber :*/ x.Inventory.Description,
                            InventoryID: x.Inventory.ID,
                            InventoryDescription: /*x.ProductID ? x.Inventory.Description + ' - ' + x.ProductNumber :*/ x.Inventory.Description,
                            InventoryCode: x.Inventory.Code,
                            Inventory: x.Inventory,
                            // ProductID: x.ProductID,
                            // ProductNumber: x.ProductNumber,
                            TaxPercentage: props.companyTaxPercentage,
                            Integrated: inventory.Integrated,
                            IntegrationMessage: inventory.IntegrationMessage,
                            LineDiscountPercentage: 0,
                            UnitPriceExclusive: priceExclusive,
                            LineTotalExclusive: lineTotalExclusive,
                            IsActive: true,
                            [props.module === Enums.Module.Quote ? 'QuoteItemType' : props.module === Enums.Module.Invoice ? 'InvoiceItemType' : props.module === Enums.Module.PurchaseOrder ? 'ItemType' : 'Unknown']: props.module === Enums.Module.Quote ? Enums.QuoteItemType.Inventory : props.module === Enums.Module.Invoice ? Enums.InvoiceItemType.Inventory : props.module === Enums.Module.PurchaseOrder ? Enums.ItemType.Inventory : Enums.ItemType.Inventory,
                            Quantity: x.Quantity,
                            InventorySectionID: section?.ID || null,
                            InventorySectionName: section?.Name || null,
                            FromBundleID: values.bundle,
                            UsePriceList: x.UsePriceList,
                            BundleDiscountedPercentage: x.PriceListDiscount,
                            HideLineItems: false,
                            DisplaySubtotal: false,
                            ...props.defaultSectionPdfSettings,
                            LineNumber: props.items.length + i + 1, // starts at 1
                            ID: crypto?.randomUUID(),
                            InventoryActive: true,
                            WarehouseID: helper.isInventoryWarehoused(x.Inventory) /*x.Inventory.IsQuantityTracked*/ ? getSelectedWarehouse()?.ID ?? null : null,
                            Warehouse: helper.isInventoryWarehoused(x.Inventory) /*x.Inventory.IsQuantityTracked*/ ? getSelectedWarehouse() : null,
                            UnitCostPrice: roundPrice(inventory.CostPrice)
                            // UnitAmount: 0,
                            // Inventory: x.Inventory,
                            // ID: crypto?.randomUUID(),
                            /*...(props.type === 'Job' ?
                                {
                                    JobCardID: props.itemID,
                                    JobCardDescription: props.jobQueryData.Description,
                                    QuantityAllocated: 0,
                                    QuantityOutstanding: 0,
                                    QuantityReturned: 0,
                                    QuantityReturnPending: 0,
                                    QuantityRequested: parseFloat(x.Quantity),
                                    StockItemStatus: props.filteredStockItemStatus,
                                    Billable: false,
                                } :
                                {  // recurring job
                                    JobScheduleID: props.itemID,
                                    Quantity: parseFloat(x.Quantity),
                                    StockItemStatus: x.Inventory.StockItemType === Enums.StockItemType.Service ? Enums.StockItemStatus.ItemUsed : Enums.StockItemStatus.WorkedOn,
                                    Billable: false,
                                }
                            )*/
                        }
                    )
                })
            ]
            props.onSaveItems(newItemsList)
            // Reset form after successful save
            resetForm();
        } else {

        }
    }

    useEffect(
        () => {
            if (props.triggerSave > 0) {
                handleSubmit(form.values)
            }
        },
        [props.triggerSave]
    )

    // Reset form after successful save
    const resetForm = () => {


        form.setValues({
            bundle: null,
            section: props.module === Enums.Module.Quote || props.module === Enums.Module.Invoice,
            sectionName: '',
            warehouse: null as any
        });

        form.resetTouched();
        form.resetDirty();

        setInventoryItems([]);
    }

    return <>
        <input style={{ opacity: 0, position: "absolute", pointerEvents: "none" }} />
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <BundleSelector
                label={'Select bundle'}
                withAsterisk
                {...form.getInputProps('bundle')}
            />
            {(props.module === Enums.Module.Quote || props.module === Enums.Module.Invoice) && <>
                <Checkbox
                    label={'Add as section'}
                    mt={'lg'}
                    size={'sm'}
                    defaultChecked={form.values.section}
                    {...form.getInputProps('section')}
                />
                <>
                    {
                        form.values.section &&
                        <ScTextControl
                            label={'Section Name'}
                            {...form.getInputProps('sectionName')}
                        />
                    }
                </>
            </>
            }


            {hasStockControl && form.values["bundle"] && props.module !== Enums.Module.JobCard &&
                <WarehouseSelector
                    required={true}
                    selectedWarehouse={form.values["warehouse"]}
                    setSelectedWarehouse={(x: any) => form.setFieldValue('warehouse', x)}
                    storeID={props.storeID}
                    filterByEmployee={true}
                    {...form.getInputProps('warehouse')}
                    hideFromView={true}
                    onSuppressSave={props.onSuppressSave}
                    ignoreIDs={[]}
                />
            }

            {
                inventoryItems.length !== 0 &&
                <Box mt={'md'} style={{ overflow: 'hidden' }}>
                    <Text fw={600} c={'scBlue.9'} size={'md'} mb={'xs'}>
                        Included items:
                    </Text>
                    {
                        inventoryItems.map((x: any) => (
                            <Flex key={x.ID} align={'center'} gap={5} px={'sm'}>
                                <Text size={'sm'}>
                                    {x.Inventory.Description}
                                </Text>
                                <Text size={'xs'} fw={500}>
                                    ({x.Quantity})
                                </Text>
                            </Flex>
                        ))
                    }
                </Box>
            }

            {hasTrackedItems && stockDataReady && hasExceededStock && (
                <Box
                    mt={'sm'}
                    px={'xs'}
                    py={6}
                    maw={500}
                    style={{ backgroundColor: '#FDD199', borderRadius: 'var(--mantine-radius-sm)' }}
                >
                    <Flex align="center" gap={8}>
                        <IconAlertCircle size={16} color="#F26101" style={{ flexShrink: 0 }} />
                        <Text size={'sm'} c={"#F26101"}>Some quantities in this bundle exceed available stock.</Text>
                    </Flex>
                </Box>
            )}

            {hasTrackedItems && stockDataReady && !hasExceededStock && (
                <Box
                    mt={'sm'}
                    px={'xs'}
                    py={6}
                    maw={500}
                    style={{ backgroundColor: '#D3F9D8', borderRadius: 'var(--mantine-radius-sm)' }}
                >
                    <Flex align="center" gap={8}>
                        <IconCheck size={16} color="#2B8A3E" style={{ flexShrink: 0 }} />
                        <Text size={'sm'} c={"#2B8A3E"}>All quantities in this bundle are within available stock.</Text>
                    </Flex>
                </Box>
            )}

            {/*<Group mt={'5rem'} justify={'right'} gap={'xs'}>
                <Button
                    type={'button'}
                    variant={'subtle'}
                    color={'gray.9'}
                    onClick={() => props.onClose()}
                >
                    Cancel
                </Button>
                <Button
                    color={'scBlue'}
                    type={'submit'}
                    disabled={inventoryItemsQuery.isFetching || !sectionQuery.isFetched || saveInventoryUsedMutation.isLoading}
                        rightSection={(saveInventoryUsedMutation.isLoading) && <Loader variant={'oval'} size={16} color={'white'} />}
                >
                    Add Bundle
                </Button>
            </Group>*/}
        </form>
    </>
}

export default AddBundleToSectionListForm
